import {
  AllToModerateVehiculeCheck,
  AllToValidateVehiculeCheck,
  TotalActiveVehiculeCheck,
} from '@/api/entities/vehicule-check';
import {
  createNewVehiculeCheck,
  deleteManyVehiculeChecks,
  deleteVehiculeCheck,
  getAllVehiculeChecks,
  getAllVehiculeChecksToModerate,
  getAllVehiculeChecksToValidate,
  getCreatedVehiculeChecksPerMonth,
  getMatchingVehiculeCheckError,
  getTotalActiveVehiculeChecks,
  getVehiculeCheckById,
  importVehiculeCheckRequests,
  updateVehiculeCheck,
  validateModeration,
  VehiculeCheckImportItem,
} from '@/api/vehicule-check/vehicule-check';
import { TVehiculeCheckSchema } from '@/schemas/vehicule-check/vehicule-check.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { NOTIFY } from '@/utils/constant.utils';
import { notify } from '@/utils/toast.utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDeleteManyVehiculeCheckResultsByVehiculeCheckId } from './vehicule-check-result.hooks';
import { HistoryAdvancedFilter } from '@/entities/history';
import { useAuth } from './auth.hooks';
import { useLocation } from 'react-router-dom';

export const useCreateVehiculeCheck = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  const { pathname } = useLocation();
  const isRequestPage = pathname.startsWith('/vehicule-check-request');

  return useMutation({
    mutationFn: (data: TVehiculeCheckSchema) => createNewVehiculeCheck(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['getCreatedVehiculeChecksPerMonth'],
      });
      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeChecks'] });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveVehiculeChecks'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getAllVehiculeChecksToModerate'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getAllVehiculeChecksToValidate'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingVehiculeCheckError'],
      });
      queryClient.invalidateQueries({ queryKey: ['getMatchingVehiculeError'] });
      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });

      if (isRequestPage) {
        notify(t('inspectionRequestCreatedSuccessfully'), NOTIFY.SUCCESS);
      } else {
        notify(t('inspectionCreatedSuccessfully'), NOTIFY.SUCCESS);
      }

      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetMatchingVehiculeErrorCheck = (
  query?: TVehiculeCheckSchema,
) => {
  return useQuery({
    queryKey: ['getMatchingVehiculeCheckError', query],
    queryFn: () => getMatchingVehiculeCheckError(query),
  });
};

// export const useGetAllVehiculeChecks = (
//   filter: VehiculeCheckFilterType,
//   page: number,
//   limit: number,
//   sortBy?: string,
//   sortDirection?: 'asc' | 'desc'
// ) => {
//   return useQuery({
//     queryKey: ['getAllVehiculeChecks', filter, page, sortBy, sortDirection],
//     queryFn: () =>
//       getAllVehiculeChecks(filter, page, limit, sortBy, sortDirection),
//   });
// };

export const useGetAllVehiculeChecks = (
  filter: HistoryAdvancedFilter,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  return useQuery({
    queryKey: [
      'getAllVehiculeChecks',
      filter,
      page,
      sortBy,
      sortDirection,
      limitedRoleLoggedInUserId,
    ],
    queryFn: () =>
      getAllVehiculeChecks(
        { ...filter, userId: limitedRoleLoggedInUserId },
        page,
        limit,
        sortBy,
        sortDirection,
      ),
  });
};

export const useGetTotalActiveVehiculeChecks = (
  query?: TotalActiveVehiculeCheck,
) => {
  return useQuery({
    queryKey: ['getTotalActiveVehiculeChecks', query],
    queryFn: () => getTotalActiveVehiculeChecks(query),
  });
};

export const useGetVehiculeCheckById = (id: string) => {
  return useQuery({
    queryKey: ['getVehiculeCheckById', id],
    queryFn: () => getVehiculeCheckById(id),
    enabled: !!id,
  });
};

export const useGetCreatedVehiculeChecksPerMonth = (userId?: string) => {
  return useQuery({
    queryKey: ['getCreatedVehiculeChecksPerMonth', userId],
    queryFn: () => getCreatedVehiculeChecksPerMonth(userId),
  });
};

export const useGetAllVehiculeChecksToModerate = (
  query?: AllToModerateVehiculeCheck,
) => {
  return useQuery({
    queryKey: [
      'getAllVehiculeChecksToModerate',
      query?.userId,
      query?.actionUserType,
    ],
    queryFn: () => getAllVehiculeChecksToModerate(query),
  });
};

export const useGetAllVehiculeChecksToValidate = (
  query?: AllToValidateVehiculeCheck,
) => {
  return useQuery({
    queryKey: [
      'getAllVehiculeChecksToValidate',
      query?.userId,
      query?.actionUserType,
    ],
    queryFn: () => getAllVehiculeChecksToValidate(query),
  });
};

export const useUpdateVehiculeCheck = ({
  t,
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  const { pathname } = useLocation();
  const isRequestPage = pathname.startsWith('/vehicule-check-request');

  return useMutation({
    mutationFn: (vehiculeCheck: TVehiculeCheckSchema) =>
      updateVehiculeCheck(vehiculeCheck),
    onSuccess: (_, variables) => {
      const { id } = variables;

      queryClient.invalidateQueries({
        queryKey: ['getCreatedVehiculeChecksPerMonth'],
      });
      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeChecks'] });
      queryClient.invalidateQueries({ queryKey: ['getVehiculeCheckById', id] });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingVehiculeCheckError'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getAllVehiculeChecksToModerate'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getAllVehiculeChecksToValidate'],
      });
      queryClient.invalidateQueries({ queryKey: ['getMatchingVehiculeError'] });
      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });

      if (isRequestPage) {
        notify(t('inspectionRequestUpdatedSuccessfully'), NOTIFY.SUCCESS);
      } else {
        notify(t('inspectionUpdatedSuccessfully'), NOTIFY.SUCCESS);
      }

      handleSuccess?.();
    },
    onError: (err: ErrorResponse) => {
      handleError?.(err);
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating vehicule check:', err.error);
    },
  });
};

export const useValidateModeration = ({
  t,
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (idVehicleCheck: string) => validateModeration(idVehicleCheck),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getCreatedVehiculeChecksPerMonth'],
      });
      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeChecks'] });
      queryClient.invalidateQueries({ queryKey: ['getVehiculeCheckById'] });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingVehiculeCheckError'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getResultByVehiculeCheckId'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getAllVehiculeChecksToModerate'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getAllVehiculeChecksToValidate'],
      });
      notify(t('moderationValidation'), NOTIFY.SUCCESS);
      handleSuccess?.();
    },
    onError: (err: ErrorResponse) => {
      handleError?.(err);
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when validating moderation:', err.error);
    },
  });
};

export const useDeleteVehiculeCheck = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  const { pathname } = useLocation();
  const isRequestListPage = pathname.startsWith('/vehicule-check-requests');

  return useMutation({
    mutationFn: (id: string) => deleteVehiculeCheck(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: ['getCreatedVehiculeChecksPerMonth'],
      });
      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeChecks'] });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveVehiculeChecks'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingVehiculeCheckError'],
      });
      queryClient.invalidateQueries({ queryKey: ['getMatchingVehiculeError'] });
      queryClient.invalidateQueries({
        queryKey: ['getAllVehiculeChecksToModerate'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getAllVehiculeChecksToValidate'],
      });

      if (isRequestListPage) {
        notify(t('inspectionRequestDeletedSuccessfully'), NOTIFY.SUCCESS);
      } else {
        notify(t('inspectionDeletedSuccessfully'), NOTIFY.SUCCESS);
      }

      handleSuccess?.({ id });
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useDeleteManyVehiculeChecks = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  const { pathname } = useLocation();
  const isRequestListPage = pathname.startsWith('/vehicule-check-requests');

  const { mutate: deleteManyVehiculeCheckResults } =
    useDeleteManyVehiculeCheckResultsByVehiculeCheckId();

  return useMutation({
    mutationFn: (ids: string[]) => deleteManyVehiculeChecks(ids),
    onSuccess: (_, ids) => {
      for (const id of ids) {
        deleteManyVehiculeCheckResults(id);
      }

      queryClient.invalidateQueries({
        queryKey: ['getCreatedVehiculeChecksPerMonth'],
      });
      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeChecks'] });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveVehiculeChecks'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingVehiculeCheckError'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getAllVehiculeChecksToModerate'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getAllVehiculeChecksToValidate'],
      });
      queryClient.invalidateQueries({ queryKey: ['getMatchingVehiculeError'] });

      if (isRequestListPage) {
        notify(
          ids.length > 1
            ? t('inspectionRequestsDeletedSuccessfully')
            : t('inspectionRequestDeletedSuccessfully'),
          NOTIFY.SUCCESS,
        );
      } else {
        notify(
          ids.length > 1
            ? t('inspectionsDeletedSuccessfully')
            : t('inspectionDeletedSuccessfully'),
          NOTIFY.SUCCESS,
        );
      }

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useImportVehiculeCheckRequests = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: VehiculeCheckImportItem[]) =>
      importVehiculeCheckRequests(items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getAllVehiculeChecks'] });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveVehiculeChecks'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getAllVehiculeChecksToModerate'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getAllVehiculeChecksToValidate'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getCreatedVehiculeChecksPerMonth'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingVehiculeCheckError'],
      });
      queryClient.invalidateQueries({ queryKey: ['getMatchingVehiculeError'] });
      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });

      handleSuccess?.();
      notify(t('inspectionRequestImportSuccess'), NOTIFY.SUCCESS);
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error importing vehicule check requests:', error);
    },
  });
};
